import { useContext, useMemo } from 'react';
import { BorderColor, ManageSearch, ReportProblem } from '@mui/icons-material';
import { AbilityContext } from 'components/Permissions/Can';
import { performAssessments, adminAssessments, assessments, testTakers, learningResources, linguisticLab, dashboard, app, users, childDevelopment, newsletters, tutorials, ticktes, tickets, assessmentLibrary, assessmentsStatus, AssessmentExecutionTracking, assessmentExecutionTracking, assessmentExecution, school, affiliations, affiliationSetup, practiceAssessments, createPracticeAssessments, systemReview, createBattery, stuckReasons, manageAssessments } from "router/routes"
import { ReactComponent as HomeIcon } from 'assets/icons/Home.svg';
import { ReactComponent as AssessmentLibraryIcon } from 'assets/icons/AssessmentLibrary.svg';
import { ReactComponent as UsersIcon } from 'assets/icons/Users.svg';
import { ReactComponent as TestTakersIcon } from 'assets/icons/Testtakers.svg';
import { ReactComponent as AssessmentsIcon } from 'assets/icons/Assessments.svg';
import { ReactComponent as ActiveAssessmentsIcon } from 'assets/icons/ActiveAssessments.svg';
import { ReactComponent as TranscriptionIcon } from 'assets/icons/Transcription.svg';
import { ReactComponent as VoiceLabIcon } from 'assets/icons/VoiceLab.svg';
import { ReactComponent as LabIcon } from 'assets/icons/Lab.svg';
import { ReactComponent as ChildDevelopmentIcon } from 'assets/icons/ChildDevelopment.svg';
import { ReactComponent as WhatNewsIcon } from 'assets/icons/WhatNews.svg';
import { ReactComponent as TutorialsIcon } from 'assets/icons/Tutorials.svg';
import { ReactComponent as MessagesIcon } from 'assets/icons/Messages.svg';
import { ReactComponent as MessagesActiveIcon } from 'assets/icons/MessagesActive.svg';
import { features } from 'keys';
import { assessmentRQ, ticketRQ } from 'store/hooks';
import { SIDEBAR_NAMES } from '../constants';
import { systemTypes } from 'keys/systems';

const useMenuItems = () => {
    const ability = useContext(AbilityContext)

    // כרגע הוצאתי מפעולה כי משפיע על הביצועים והאינדצקייה לא מספקת בכלל אז אין צורך בפיצר
    // const watingForMarkingCount = assessmentRQ.useWatingForMarkingCount()
    const ticketCount = ticketRQ.useTicketCount()

    const menuItems = useMemo(
        () => [
            // ==========================================
            // קבוצה 1: מסכי בסיס
            // ==========================================
            {
                name: SIDEBAR_NAMES.dashboard,
                label: "בית",
                path: dashboard,
                icon: HomeIcon,
                system: systemTypes.school,
                feature: "dashboard",
                cleanTestTaker: true,
            },

            // ==========================================
            // קבוצה 2: ביצוע מבדקים, תרגולים ודוחות
            // ==========================================
            {
                name: SIDEBAR_NAMES.performAssessments,
                label: "מבדקים פעילים",
                path: performAssessments,
                icon: ActiveAssessmentsIcon,
                system: systemTypes.school,
                feature: "perform-assessments",
                cleanTestTaker: true,
            },
            {
                name: SIDEBAR_NAMES.practiceAssessments,
                label: "תרגולים",
                path: practiceAssessments,
                icon: ActiveAssessmentsIcon,
                system: systemTypes.school,
                feature: features.practicate_assessments,
                cleanTestTaker: true,
                subItems: [
                    {
                        name: SIDEBAR_NAMES.performPractices,
                        label: "תרגולים פעילים",
                        path: practiceAssessments,
                        feature: features.practicate_assessments,
                    },
                    {
                        name: SIDEBAR_NAMES.createPractices,
                        label: "הוספת תרגולים",
                        path: createPracticeAssessments,
                        feature: features.practicate_assessments,
                    }
                ]
            },
            {
                name: SIDEBAR_NAMES.assessments,
                label: "דוחות",
                path: assessments,
                icon: AssessmentsIcon,
                system: systemTypes.school,
                feature: "assessments",
                cleanTestTaker: true,
            },
            {
                name: SIDEBAR_NAMES.systematicReview,
                label: "סקירה מערכתית",
                path: systemReview,
                icon: TranscriptionIcon,
                system: systemTypes.school,
                feature: features.general_performance_review,
                hide: true,
                cleanTestTaker: true,
                subItems: [
                    {
                        name: SIDEBAR_NAMES.assessmentExecution,
                        label: "נתוני ביצוע",
                        path: app.assessmentExecutionTracking,
                        feature: features.general_performance_review,
                    },
                    {
                        name: SIDEBAR_NAMES.generalPerformanceReview,
                        label: "סטטיסטיקות",
                        path: app.assessmentsStatus,
                        feature: features.general_performance_review
                    },
                    {
                        name: SIDEBAR_NAMES.achievementGraph,
                        label: "מעקב התקדמות",
                        path: app.achievementGraph,
                        feature: features.general_performance_review
                    },
                    {
                        name: SIDEBAR_NAMES.progressGraphs,
                        label: "גרפי התקדמות",
                        path: app.progressGraphs,
                        feature: features.general_performance_review
                    }
                ]
            },

            // ==========================================
            // קבוצה 3: ניהול לקוחות, משתמשים ותלמידים
            // ==========================================
            {
                name: SIDEBAR_NAMES.testTakers,
                label: "תלמידים",
                path: testTakers,
                icon: TestTakersIcon,
                system: systemTypes.school,
                feature: "test-takers",
                cleanTestTaker: true,
            },
            // סופר יוזר רואה את כל בתי הספר, לעומת מנהל בית ספר שרואה רק את הנתונים שלו
            ability.can('manage', 'super-user') ?
                {
                    name: SIDEBAR_NAMES.affiliations,
                    label: "ניהול לקוחות",
                    path: affiliations,
                    icon: UsersIcon,
                    system: systemTypes.school,
                    feature: 'super-user',
                    role: "manage",
                } :
                {
                    name: SIDEBAR_NAMES.affiliationManagement,
                    label: "פרטי בית הספר",
                    path: affiliations,
                    icon: UsersIcon,
                    system: systemTypes.school,
                    feature: features.test_takers,
                },
            {
                name: SIDEBAR_NAMES.users,
                label: "משתמשים",
                path: users,
                icon: UsersIcon,
                system: systemTypes.school,
                feature: "users",
            },

            // ==========================================
            // קבוצה 4: ניהול מערכות אדמין, מעבדות וספריות
            // ==========================================
            {
                name: SIDEBAR_NAMES.transcriptionSystem,
                label: "מערכת תעתוק",
                path: adminAssessments,
                icon: TranscriptionIcon,
                system: systemTypes.administrative,
                feature: "transcription_system",
                hide: true,
                cleanTestTaker: true,
                // badge: watingForMarkingCount.data,
                subItems: [
                    {
                        name: SIDEBAR_NAMES.primaryAssessments,
                        label: "סימון מילים",
                        path: app.markingWordsAssessments,
                        feature: "admin_assessment",
                    },
                    {
                        name: SIDEBAR_NAMES.trainingAssessments,
                        label: "מבדקי לימוד",
                        path: app.markingTrainingWordsAssessments,
                        feature: "training_assessments",
                    },
                    {
                        name: SIDEBAR_NAMES.audioCutting,
                        label: "סימון אודיו",
                        path: app.audioCuttingAssessments,
                        feature: "audio-cutting",
                    },
                    {
                        name: SIDEBAR_NAMES.transcription,
                        label: "תעתוק",
                        path: app.transcriptionAssessments,
                        feature: "transcription"
                    },

                ]
            },
            // כל הפריט הזה (כולל ה-subItems) מיועד רק לסופר יוזר ולמי שקיבל הרשאת stuck_reasons_admin -
            // חייב תנאי ברמת ה-array (לא רק feature/role על האובייקט) כי SideBarItems.js תמיד
            // מעביר role="read" קשיח ל-<Can>, אז feature/role על הפריט עצמו לא באמת חוסמים כלום
            ...(ability.can('manage', 'stuck_reasons_admin') ? [{
                name: SIDEBAR_NAMES.transcriptionManage,
                label: "ניהול תעתוק",
                path: manageAssessments,
                icon: TranscriptionIcon,
                system: systemTypes.administrative,
                feature: "transcription_system",
                hide: true,
                cleanTestTaker: true,
                // badge: watingForMarkingCount.data,
                subItems: [
                    // "עלות מבדקים" - רק לסופר יוזר, לא לשני
                    ...(ability.can('manage', 'super-user') ? [{
                        name: SIDEBAR_NAMES.assessmentCosts,
                        label: "עלות מבדקים",
                        path: app.assessmentCosts,
                        feature: "admin_assessment",
                    }] : []),
                    {
                        name: SIDEBAR_NAMES.stuckReasons,
                        label: "ניהול הערות",
                        path: stuckReasons,
                        icon: ReportProblem,
                        feature: 'stuck_reasons_admin',
                    }
                ]
            }] : []),
            {
                name: SIDEBAR_NAMES.voiceLab,
                label: "המעבדה הקולית",
                path: learningResources,
                icon: VoiceLabIcon,
                system: systemTypes.administrative,
                feature: "voice-lab"
            },
            {
                name: SIDEBAR_NAMES.linguisticLab,
                label: "המעבדה הלשונית",
                path: linguisticLab,
                icon: LabIcon,
                system: systemTypes.administrative,
                feature: "linguistic-lab",
                hide: true,
                subItems: [
                    {
                        name: SIDEBAR_NAMES.textReadability,
                        label: "קריאות טקסטים",
                        path: app.textReadability,
                        icon: ManageSearch,
                        feature: "admin-assessments",
                    },
                    {
                        name: SIDEBAR_NAMES.wordReadability,
                        label: "קריאות מילים",
                        path: app.wordReadability,
                        icon: ManageSearch,
                        feature: "admin-assessments",
                    }
                ]
            },
            {
                name: SIDEBAR_NAMES.childDevelopment,
                label: "התפתחות הילד",
                path: childDevelopment,
                icon: ChildDevelopmentIcon,
                system: systemTypes.administrative,
                feature: "child-development",
                hide: true,
                subItems: [
                    {
                        name: SIDEBAR_NAMES.normalizer,
                        label: "נורמלטור",
                        path: app.normalizer,
                        icon: BorderColor,
                        feature: "child-development",
                    }
                ]
            },
            {
                name: SIDEBAR_NAMES.assessmentLibrary,
                label: "ספריית מבדקים",
                path: assessmentLibrary,
                icon: AssessmentLibraryIcon,
                system: systemTypes.administrative,
                feature: "assessment-library",
                subItems: [
                    {
                        name: SIDEBAR_NAMES.createAssessmentDef,
                        label: "יצירת מבדק",
                        path: app.createAssessmentDef,
                        feature: "assessment-library",
                    },
                    {
                        name: SIDEBAR_NAMES.createAssessments,
                        label: "הקצאת מבדקים",
                        path: app.createAssessments,
                        feature: "assessment-library",
                    },
                    {
                        name: SIDEBAR_NAMES.createBattery,
                        label: "יצירת סוללה",
                        path: app.createBattery,
                        feature: "assessment-library",
                    }
                ]
            },



            // ==========================================
            // קבוצה 5: תקשורת, עזרה ופניות
            // ==========================================
            {
                name: SIDEBAR_NAMES.tutorials,
                label: "הדרכות",
                path: tutorials,
                icon: TutorialsIcon,
                system: systemTypes.administrative,
                feature: "tutorials"
            },
            {
                name: SIDEBAR_NAMES.newsletters,
                label: "מה חדש?",
                path: newsletters,
                icon: WhatNewsIcon,
                system: systemTypes.administrative,
                feature: "newsletter"
            },
            {
                name: SIDEBAR_NAMES.tickets,
                label: "פניות",
                path: tickets,
                icon: ticketCount.data ? MessagesActiveIcon : MessagesIcon,
                system: systemTypes.school,
                feature: "tickets",
                badge: ticketCount.data,
            }
        ], [
        // watingForMarkingCount.data,
        ticketCount.data,
        ability
    ])

    return menuItems
}

export default useMenuItems